import { Spacing } from '@persona-id/design-tokens';
import { UsersIcon } from '@persona-id/design-tokens/icons';
import { CircleHelpIcon } from '@persona-id/design-tokens/icons/12px';
import { DateTime } from 'luxon';
import styled, { css } from 'styled-components';

import HoverCopyableField from 'dashboard/components/HoverCopyableField';
import Permissioned from 'dashboard/components/Permissioned';
import Pii from 'dashboard/components/Pii';
import ReportStatusSticker from 'dashboard/components/ReportStatusSticker';
import { MonoSpan } from 'dashboard/components/Typography';
import TableLayout, {
  TableLinkTR,
  TableNullState,
  TableTD,
  TableTH,
} from 'dashboard/layouts/TableLayout';
import { SortParams } from 'dashboard/lib/interfaces/SortParams';
import { Report } from 'dashboard/models/report';
import Spacer from 'design-system/components/Spacer';
import { Paragraph } from 'design-system/components/Text';
import Tooltip from 'design-system/components/Tooltip';
import NullStateReports from 'images/dashboard/i16-file-text.svg';
import IconEmail from 'images/icons/email.svg';
import IconFileText from 'images/icons/file-text.svg';
import IconGlobe from 'images/icons/globe.svg';
import IconGovernment from 'images/icons/government.svg';
import IconPhone from 'images/icons/phone.svg';
import IconUser from 'images/icons/user.svg';
import { DATE_AND_TIME_FORMAT, TIMEZONE_NAME_ABBREVIATED } from 'lib/constants/luxonFormats';
import usePermission from 'lib/hooks/usePermission';
import { ApiModel } from 'lib/interfaces/api';
import { Permission } from 'lib/permissions';
import isEmpty from 'lib/utils/formValidation/isEmpty';
import { assertNever } from 'lib/utils/types';

const ICON_STYLES = css`
  width: 16px;
  margin-right: ${Spacing[1]};
  flex-shrink: 0;
  > * {
    stroke: ${({ theme }) => theme.color.text.tertiary};
  }
`;

const ReportTemplateTypeIconEmail = styled(IconEmail)`
  ${ICON_STYLES}
`;

const ReportTemplateTypeIconFileText = styled(IconFileText)`
  ${ICON_STYLES}
`;

const ReportTemplateTypeIconGlobe = styled(IconGlobe)`
  ${ICON_STYLES}
`;

const ReportTemplateTypeIconPhone = styled(IconPhone)`
  ${ICON_STYLES}
`;

const StyledCircle = styled(CircleHelpIcon)`
  margin-left: ${Spacing[1]};
  flex-shrink: 0;
  color: ${({ theme }) => theme.color.text.tertiary};
`;

const ReportTemplateTypeIconGovernment = styled(IconGovernment)`
  ${ICON_STYLES}
`;

const ReportTemplateTypeIconUser = styled(IconUser)`
  ${ICON_STYLES}
`;

const ReportTemplateTypeIconSocialMedia = styled(UsersIcon)`
  margin-right: ${Spacing[1]};
  flex-shrink: 0;
  color: ${({ theme }) => theme.color.text.tertiary};
`;

const ReportTemplateTypeIconProfileNonAuthoritative = styled(UsersIcon)`
  margin-right: ${Spacing[1]};
  flex-shrink: 0;
  color: ${({ theme }) => theme.color.text.tertiary};
`;

const TooltipContainer = styled('div')`
  max-width: 500px;
`;

const ResolutionContainer = styled('div')`
  max-width: 400px;
`;

const ReportTemplateTypeRow = styled(Paragraph)`
  display: flex;
  align-items: center;
`;

const ReportRowTableLinkTR = styled(TableLinkTR)`
  height: 61px;
`;

const ReportRowTableTD = styled(TableTD)`
  padding: ${Spacing[2]} ${Spacing[2]} ${Spacing[2]} ${Spacing[4]};
`;

export enum ReportsTableColumns {
  PrimaryInput = 'Primary Input',
  Status = 'Status',
  ReportId = 'Report ID',
  CreatedBy = 'Created By',
  CreatedAt = 'Created At',
  LastUpdatedAt = 'Last Updated At',
}

interface ReportsTableProps {
  reports: Report[];
  sortParams: SortParams;
  isLoading: boolean;
  hasUsedFilters: boolean;
  tableLinkPrefix?: string;
  handleSortUpdate: (sortParams: SortParams) => void;
  visibleColumns?: Array<ReportsTableColumns>;
}

const ReportsTable = ({
  reports,
  sortParams,
  isLoading,
  hasUsedFilters,
  tableLinkPrefix,
  handleSortUpdate,
  visibleColumns = [
    ReportsTableColumns.PrimaryInput,
    ReportsTableColumns.Status,
    ReportsTableColumns.ReportId,
    ReportsTableColumns.CreatedAt,
  ],
}: ReportsTableProps) => {
  const handleColumnHeaderClick = (columnName: string) => {
    handleSortUpdate({
      sort: columnName,
      shouldSortDescending:
        columnName === sortParams.sort ? !sortParams.shouldSortDescending : true,
    });
  };

  const reportRows = reports.map((report) => {
    const reportType = report.type;
    if (reportType == null) {
      return null;
    }

    return (
      <ReportRowTableLinkTR
        to={tableLinkPrefix ? `${tableLinkPrefix}/${report.id}` : report.id}
        data-test={`reports-table__row--${report.id}`}
        id={`reports-table__row--${report.id}`}
        key={report.id}
      >
        {visibleColumns.includes(ReportsTableColumns.PrimaryInput) && (
          <ReportRowTableTD>
            <Pii hideOnRedacted={false} isRedacted={!isEmpty(report.attributes['redacted-at'])}>
              {report.attributes['primary-input'] || '-'}
            </Pii>
            <ReportTemplateTypeRow size='xs' color='tertiary'>
              {reportIcon(reportType)}
              <span>{reportTypeDisplay(reportType)}</span>
            </ReportTemplateTypeRow>
          </ReportRowTableTD>
        )}
        {visibleColumns.includes(ReportsTableColumns.Status) && (
          <ReportRowTableTD>
            <ResolutionContainer>
              {/* @ts-expect-error [strict-migration] The following line is not strict; please fix! */}
              <ReportStatusSticker report={report} />
            </ResolutionContainer>
          </ReportRowTableTD>
        )}
        {visibleColumns.includes(ReportsTableColumns.ReportId) && (
          <ReportRowTableTD>
            <HoverCopyableField copyValue={report.id} confirmationText='Copied Report ID'>
              <MonoSpan>{report.id}</MonoSpan>
            </HoverCopyableField>
          </ReportRowTableTD>
        )}
        {visibleColumns.includes(ReportsTableColumns.CreatedBy) && (
          <ReportRowTableTD>{report.attributes['creator-name'] ?? 'N/A'}</ReportRowTableTD>
        )}
        {visibleColumns.includes(ReportsTableColumns.CreatedAt) && (
          <ReportRowTableTD>
            {report.attributes['created-at']
              ? DateTime.fromISO(report.attributes['created-at']).toFormat(DATE_AND_TIME_FORMAT)
              : '–'}
          </ReportRowTableTD>
        )}
        {visibleColumns.includes(ReportsTableColumns.LastUpdatedAt) && (
          <ReportRowTableTD>
            {report.attributes['completed-at']
              ? DateTime.fromISO(report.attributes['completed-at']).toFormat(DATE_AND_TIME_FORMAT)
              : '–'}
          </ReportRowTableTD>
        )}
      </ReportRowTableLinkTR>
    );
  });

  const hasListPermission = usePermission(Permission.ReportList);

  return (
    <TableLayout
      dataTest='reports-table'
      isLoading={isLoading}
      showHeader={true}
      isEmpty={reports.length === 0 || !hasListPermission}
      nullState={
        <TableNullState hasUsedFilters={hasUsedFilters} dataTestBlock='reports-view'>
          <NullStateReports />
          <Spacer height={3} />
          <Permissioned
            permission={Permission.ReportView}
            fallback={
              <div>
                User permission required to view report details. Please contact your account admin
                if you&#39;d like to view report details.
              </div>
            }
          >
            <div>
              Gather additional information about an individual using watchlists, adverse media, and
              other reports.
            </div>
          </Permissioned>
          <Spacer height={5} />
          <a href='https://docs.withpersona.com/docs/reports' target='_blank' rel='noreferrer'>
            Learn more about reports
          </a>
        </TableNullState>
      }
      headerTR={
        <>
          {visibleColumns.includes(ReportsTableColumns.PrimaryInput) && (
            <TableTH data-test='reports-table__type-header'>
              Primary Input
              <Tooltip side='right' iconTrigger={<StyledCircle />}>
                <TooltipContainer>
                  <Paragraph>
                    Each Report Template has a primary input used to run the report.
                  </Paragraph>
                </TooltipContainer>
              </Tooltip>
            </TableTH>
          )}
          {visibleColumns.includes(ReportsTableColumns.Status) && (
            <TableTH data-test='reports-table__status-header'>Status</TableTH>
          )}
          {visibleColumns.includes(ReportsTableColumns.ReportId) && (
            <TableTH data-test='reports-table__report-id-header'>
              Report ID{' '}
              <Tooltip side='right' iconTrigger={<StyledCircle />}>
                <TooltipContainer>
                  <Paragraph>
                    A Report ID is a uniqe ID generated by Persona for each report
                  </Paragraph>
                </TooltipContainer>
              </Tooltip>
            </TableTH>
          )}
          {visibleColumns.includes(ReportsTableColumns.CreatedBy) && (
            <TableTH data-test='reports-table__report-created-by-header'>Created By</TableTH>
          )}
          {visibleColumns.includes(ReportsTableColumns.CreatedAt) && (
            <TableTH
              onClick={() => handleColumnHeaderClick('created_at')}
              canSort={true}
              isSorted={sortParams.sort === 'created_at'}
              isDescending={sortParams.shouldSortDescending}
              id='reports-table__created-at-header'
              data-test='reports-table__created-at-header'
            >
              {`Created at (${DateTime.local().toFormat(TIMEZONE_NAME_ABBREVIATED)})`}
            </TableTH>
          )}
          {visibleColumns.includes(ReportsTableColumns.LastUpdatedAt) && (
            <TableTH
              onClick={() => handleColumnHeaderClick('updated_at')}
              canSort={true}
              isSorted={sortParams.sort === 'updated_at'}
              isDescending={sortParams.shouldSortDescending}
              id='reports-table__last-updated-at-header'
              data-test='reports-table__last-updated-at-header'
            >
              {`Last updated at (${DateTime.local().toFormat(TIMEZONE_NAME_ABBREVIATED)})`}
            </TableTH>
          )}
        </>
      }
    >
      {reportRows}
    </TableLayout>
  );
};

export const reportIcon = (reportType: ApiModel) => {
  switch (reportType) {
    case ApiModel.ReportEmailAddress:
      return <ReportTemplateTypeIconEmail />;
    case ApiModel.ReportPhoneNumber:
      return <ReportTemplateTypeIconPhone />;
    case ApiModel.ReportPoliticallyExposedPerson:
      return <ReportTemplateTypeIconGovernment />;
    case ApiModel.ReportProfile:
      return <ReportTemplateTypeIconUser />;
    case ApiModel.ReportProfileNonAuthoritative:
      return <ReportTemplateTypeIconProfileNonAuthoritative />;
    case ApiModel.ReportSocialMedia:
      return <ReportTemplateTypeIconSocialMedia />;
    case ApiModel.ReportWatchlist:
      return <ReportTemplateTypeIconGlobe />;
    default:
      return <ReportTemplateTypeIconFileText />;
  }
};

export const reportTypeDisplay = (reportType: ApiModel) => {
  switch (reportType) {
    case ApiModel.ReportAddressLookup:
      return 'Address Lookup Report';
    case ApiModel.ReportAdverseMedia:
      return 'Adverse Media Report';
    case ApiModel.ReportBusinessAdverseMedia:
      return 'Business Adverse Media Report';
    case ApiModel.ReportBusinessAssociatedPersons:
      return 'Business Associated Persons Report';
    case ApiModel.ReportBusinessClassification:
      return 'Business Classification Report';
    case ApiModel.ReportBusinessIndustryClassification:
      return 'Business Industry Classification Report';
    case ApiModel.ReportBusinessRegistrationsLookup:
      return 'Business Registrations Report';
    case ApiModel.ReportBusinessLiensLookup:
      return 'Business Liens Report';
    case ApiModel.ReportBusinessLookup:
      return 'Business Lookup Report';
    case ApiModel.ReportBusinessNonprofit:
      return 'Business Nonprofit Report';
    case ApiModel.ReportBusinessOnlinePresence:
      return 'Business Online Presence Report';
    case ApiModel.ReportBusinessSocialMedia:
      return 'Business Social Media Report';
    case ApiModel.ReportBusinessWatchlist:
      return 'Business Watchlist Report';
    case ApiModel.ReportBusinessWebsite:
      return 'Business Website Report';
    case ApiModel.ReportChainalysisAddressScreening:
      return 'Chainalysis Address Screening Report';
    case ApiModel.ReportClearbitBusinessLookup:
      return 'Clearbit Report';
    case ApiModel.ReportCryptoAddressWatchlist:
      return 'Crypto Address Watchlist Report';
    case ApiModel.ReportCustomList:
      return 'Custom List Report';
    case ApiModel.ReportEmailAddress:
      return 'Email Risk Report';
    case ApiModel.ReportEquifaxOneview:
      return 'Equifax OneView Report';
    case ApiModel.ReportFinraBrokerCheck:
      return 'FINRA Broker Check Report';
    case ApiModel.ReportKyckrBusinessLookup:
      return 'Kyckr Business Lookup';
    case ApiModel.ReportMiddeskBusinessLookup:
      return 'Middesk Business Lookup';
    case ApiModel.ReportMxAccount:
      return 'Mx Account';
    case ApiModel.ReportPhoneNumber:
      return 'Phone Risk Report';
    case ApiModel.ReportPoliticallyExposedPerson:
      return 'Politically Exposed Person Report';
    case ApiModel.ReportProfile:
      return 'Profile Report';
    case ApiModel.ReportProfileNonAuthoritative:
      return 'Profile Non-Authoritative Report';
    case ApiModel.ReportScreening:
      return 'Screening Report';
    case ApiModel.ReportSecActionLookup:
      return 'SEC Action Lookup Report';
    case ApiModel.ReportSentilinkApplicationRisk:
      return 'SentiLink Application Risk';
    case ApiModel.ReportSentilinkScores:
      return 'SentiLink Scores';
    case ApiModel.ReportSocialMedia:
      return 'Social Media Report';
    case ApiModel.ReportTrmWalletScreening:
      return 'TRM Wallet Screening Report';
    case ApiModel.ReportSynthetic:
      return 'Synthetic Report';
    case ApiModel.ReportWatchlist:
      return 'Watchlist Report';
    default:
      assertNever(reportType as never);
  }
};

export default ReportsTable;
